// src/redux/supportSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  addDoc,
  collection,
  serverTimestamp,
  onSnapshot,
  query,
  orderBy,
  where,
  Unsubscribe,
  increment,
  getDocs,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import type { RootState } from "./store";

// ───────────────── TYPES ─────────────────
export interface SupportMessage {
  id?: string;
  senderId: string;
  text: string;
  type: "text";
  timestamp: any;
  senderName?: string;
  senderProfilePicture?: string;
  status: Record<string, string>;
}

export interface SupportThread {
  id: string;
  userId: string;
  supportId: string;
  lastMessage?: SupportMessage;
  unread: { [userId: string]: number };
  updatedAt: any;
}

interface SupportState {
  thread: SupportThread | null;
  messages: SupportMessage[];
  loading: boolean;
  error: string | null;
  allThreads?: SupportThread[];
}

const initialState: SupportState = {
  thread: null,
  messages: [],
  loading: false,
  error: null,
  allThreads: [],
};

// ───────────────── FIREBASE ─────────────────
const db = getFirestore();
const auth = getAuth();

// ───────────────── HELPERS ─────────────────
export const fetchSupportAgent = async (): Promise<string> => {
  const q = query(collection(db, "users"), where("isSupport", "==", true));
  const snap = await getDocs(q);

  if (!snap.empty) return snap.docs[0].id;

  const q2 = query(collection(db, "users"), where("isMaster", "==", true));
  const snap2 = await getDocs(q2);

  if (!snap2.empty) return snap2.docs[0].id;

  throw new Error("No support agent found");
};

// ───────────────── THUNKS ─────────────────

// CREATE OR GET SUPPORT THREAD
export const createOrGetSupportThread = createAsyncThunk<
  SupportThread,
  void
>("support/createOrGet", async (_, { rejectWithValue }) => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) throw new Error("User not authenticated");

    const supportId = await fetchSupportAgent();
    const threadId = `support_${userId}`;
    const threadRef = doc(db, "supportThreads", threadId);
    const snapshot = await getDoc(threadRef);

    if (snapshot.exists()) {
      const data = snapshot.data();
      return {
        id: threadId,
        userId,
        supportId,
        ...data,
        updatedAt: data.updatedAt?.toMillis?.() || null,
      } as SupportThread;
    }

    await setDoc(threadRef, {
      id: threadId,
      userId,
      supportId,
      lastMessage: null,
      unread: { [userId]: 0, [supportId]: 0 },
      updatedAt: serverTimestamp(),
    });

    const created = await getDoc(threadRef);
    return {
      id: threadId,
      userId,
      supportId,
      ...created.data(),
    } as SupportThread;
  } catch (err: any) {
    console.error("🔥 createOrGetSupportThread:", err);
    return rejectWithValue(err.message);
  }
});

// SEND SUPPORT MESSAGE
export const sendSupportMessage = createAsyncThunk<
  void,
  { text: string },
  { state: RootState }
>("support/sendMessage", async ({ text }, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const thread = state.support.thread;

    if (!thread) throw new Error("Support thread not initialized");

    const sender = state.auth.user;
    if (!sender?.userId) throw new Error("User not authenticated");

    const messagesRef = collection(db, "supportThreads", thread.id, "messages");

    const msg = {
      senderId: sender.userId,
      senderName: sender.firstName || "User",
      senderProfilePicture: sender.profilePicture || null,
      text,
      type: "text",
      status: {},
      timestamp: serverTimestamp(),
    };

    const msgDoc = await addDoc(messagesRef, msg);
    const saved = await getDoc(msgDoc);

    const receiverId =
      sender.userId === thread.userId ? thread.supportId : thread.userId;

    await updateDoc(doc(db, "supportThreads", thread.id), {
      lastMessage: {
        id: msgDoc.id,
        ...saved.data(),
      },
      updatedAt: serverTimestamp(),
      [`unread.${sender.userId}`]: 0,
      [`unread.${receiverId}`]: increment(1),
    });
  } catch (err: any) {
    console.error("🔥 sendSupportMessage error:", err);
    return rejectWithValue(err.message);
  }
});

// LISTEN TO MESSAGES
export const listenToSupportMessages = createAsyncThunk<
  Unsubscribe | void,
  { threadId: string }
>("support/listenToMessages", async ({ threadId }, { dispatch, rejectWithValue }) => {
  try {
    const messagesRef = collection(db, "supportThreads", threadId, "messages");
    const q = query(messagesRef, orderBy("timestamp", "asc"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs: SupportMessage[] = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();

        return {
          id: docSnap.id,
          senderId: data.senderId || "",
          text: data.text || "",
          type: data.type || "text",
          status: data.status || {},
          timestamp: data.timestamp?.toMillis?.() || null,
          senderName: data.senderName || "",
          senderProfilePicture: data.senderProfilePicture || null,
        };
      });

      dispatch(setSupportMessages(msgs));
    });

    return unsubscribe;
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});


// LISTEN TO ALL SUPPORT THREADS
export const listenAllSupportThreads = createAsyncThunk<
  Unsubscribe | void,
  { supportId: string }
>("support/listenAllSupportThreads", async ({ supportId }, { dispatch, rejectWithValue }) => {
  try {
    const q = query(
      collection(db, "supportThreads"),
      where("supportId", "==", supportId),
      orderBy("updatedAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const threads: SupportThread[] = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();

        let lastMessage: SupportMessage | undefined = undefined;

        if (data.lastMessage) {
          const lm = data.lastMessage;
          lastMessage = {
            id: lm.id || "",
            senderId: lm.senderId || "",
            text: lm.text || "",
            type: lm.type || "text",
            status: lm.status || {},
            timestamp: lm.timestamp?.toMillis?.() || null,
            senderName: lm.senderName || "",
            senderProfilePicture: lm.senderProfilePicture || null,
          };
        }

        return {
          id: docSnap.id,
          userId: data.userId,
          supportId: data.supportId,
          unread: data.unread || {},
          lastMessage,
          updatedAt: data.updatedAt?.toMillis?.() || null,
        };
      });

      dispatch(setAllSupportThreads(threads));
    });

    return unsubscribe;
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});



// LISTEN TO LAST MESSAGE
export const listenSupportLastMessage = createAsyncThunk<
  Unsubscribe | void,
  { threadId: string }
>("support/listenLastMessage", async ({ threadId }, { dispatch, rejectWithValue }) => {
  try {
    const threadRef = doc(db, "supportThreads", threadId);

    const unsubscribe = onSnapshot(threadRef, (snap) => {
      if (!snap.exists()) return;

      const data = snap.data();
      if (!data.lastMessage) return;

      dispatch(
        updateSupportLastMessage({
          lastMessage: {
            ...data.lastMessage,
            timestamp: data.lastMessage.timestamp?.toMillis?.() || null,
          },
        })
      );
    });

    return unsubscribe;
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});

// ───────────────── SLICE ─────────────────
const supportSlice = createSlice({
  name: "support",
  initialState,
  reducers: {
    setSupportThread: (state, action: PayloadAction<SupportThread>) => {
      state.thread = action.payload;
    },
    setSupportMessages: (state, action: PayloadAction<SupportMessage[]>) => {
      state.messages = action.payload;
    },
    updateSupportLastMessage: (
      state,
      action: PayloadAction<{ lastMessage: SupportMessage }>
    ) => {
      if (state.thread) {
        state.thread.lastMessage = action.payload.lastMessage;
        state.thread.updatedAt = Date.now();
      }
    },
    setAllSupportThreads: (state, action: PayloadAction<SupportThread[]>) => {
      state.allThreads = action.payload;
    },
  },

  // ✅ THE IMPORTANT FIX
  extraReducers: (builder) => {
    builder.addCase(createOrGetSupportThread.fulfilled, (state, action) => {
      state.thread = action.payload; // 🔥 NOW THREAD IS STORED
    });
  },
});

export const {
  setSupportThread,
  setSupportMessages,
  updateSupportLastMessage,
  setAllSupportThreads,
} = supportSlice.actions;

export default supportSlice.reducer;
